/* Design tokens. The single source of truth for the palette, spacing and
   typography scales shared by every app. Import this, not a local copy. */

:root {
  --color-primary: #e60576;
  --color-primary-20: #e6057633;

  --color-secondary: #367bff;
  --color-secondary-10: #367bff1a;
  --color-secondary-60: #367bff3c;

  --color-success: #38c2b8;
  --color-success-10: #38c2b81a;
  --color-success-60: #38c2b83c;

  --color-error: #eb5163;
  --color-error-10: #eb51631a;
  --color-error-60: #eb51633c;

  --color-warning: #f8a72d;
  --color-warning-10: #f8a72d1a;
  --color-warning-60: #f8a72d3c;

  --color-grey-1: #e4e6ea;
  --color-grey-2: #cacdd5;
  --color-grey-3: #afb4c0;
  --color-grey-4: #959bab;
  --color-grey-5: #7a8296;
  --color-grey-6: #626878;
  --color-grey-7: #494e5a;
  --color-grey-7-5: #3c404a;
  --color-grey-8: #31343c;
  --color-grey-8-5: #25272d;
  --color-grey-8-75: #1d1f24;
  --color-grey-9: #181a1e;
  --color-grey-9-25: #141619;
  --color-grey-9-5: #0c0d0f;

  --color-text-grey-light: #aaa;
  --color-text-grey-dark: #3f4846;

  --transition-bg: background 0.1s ease-out 0s,
    box-shadow 0.15s cubic-bezier(0.47, 0.03, 0.49, 1.38) 0s;

  --bg-color-light: #27282b;
  --bg-color-dark: #1f2023;
  --bg-color-middle: #19191c;
  --bg-color-middle-hover: #27272b;
  --bg-color-dark-black: #000;

  --header-bg-color: #0a0909;
  --header-bg-color-active: #1f1f22;
  --header-project-color: #8a8f98;
  --header-height: 6.8rem;

  --tab-bar-height: 4rem;

  --home-drawer-bg-color: #27282b;
  --card-bg-color: #27282b;
  --card-bg-color-current: #d1d1d1;
  --card-border-color: #000000;
  --card-subtitle-color: #8a8f98;
  --card-subtitle-color-low: rgba(138, 143, 152, 0.4);
  --icon-hover: #a7b0ba;
  --card-header-color: #1f1f22;

  --button-bgcolor-default: var(--color-grey-8-5);
  --button-bgcolor-default-hover: var(--color-grey-7-5);
  --button-bgcolor-default-focus: var(--color-grey-7-5);
  --button-bgcolor-default-active: var(--color-grey-7-5);

  --button-bgcolor-primary: var(--color-secondary);
  --button-bgcolor-primary-hover: #0b53dc;
  --button-bgcolor-primary-focus: #0b53dc;
  --button-bgcolor-primary-active: #0b53dc;

  --button-bgcolor-success: var(--color-success);
  --button-bgcolor-success-hover: var(--color-success-60);
  --button-bgcolor-success-focus: var(--color-success-60);
  --button-bgcolor-success-active: var(--color-success-60);

  --button-bgcolor-warning: var(--color-warning);
  --button-bgcolor-warning-hover: var(--color-warning-60);
  --button-bgcolor-warning-focus: var(--color-warning-60);
  --button-bgcolor-warning-active: var(--color-warning-60);

  --button-bgcolor-danger: var(--color-error);
  --button-bgcolor-danger-hover: var(--color-error-60);
  --button-bgcolor-danger-focus: var(--color-error-60);
  --button-bgcolor-danger-active: var(--color-error-60);

  --elevation-1-dp: 0 1px 1px 0 rgba(0, 0, 0, 0.14), 0 2px 1px -1px rgba(0, 0, 0, 0.12),
    0 1px 3px 0 rgba(0, 0, 0, 0.2);
  --elevation-2-dp: 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 3px 1px -2px rgba(0, 0, 0, 0.12),
    0 1px 5px 0 rgba(0, 0, 0, 0.2);
  --elevation-8-dp: 0 8px 10px 1px rgba(0, 0, 0, 0.14), 0 3px 14px 2px rgba(0, 0, 0, 0.12),
    0 5px 5px -3px rgba(0, 0, 0, 0.2);
  --elevation-16-dp: 0 16px 24px 2px rgba(0, 0, 0, 0.14), 0 6px 30px 5px rgba(0, 0, 0, 0.12),
    0 8px 10px -5px rgba(0, 0, 0, 0.2);

  --button-rest-elevation: var(--elevation-2-dp);
  --button-hover-elevation: var(--elevation-16-dp);
  --button-pressed-elevation: var(--elevation-8-dp);

  --creative-space-hover: rgba(138, 143, 152, 0.3);
  --creative-space-list-header: #5f6164;

  --text-color-white: #f9f9f9;

  --drawer-width: 22rem;
  --drawer-width-closed: 6.4rem;

  --storyboard-drawer-width: 40rem;
  --storyboard-shot-card-bg-color: #303236;
  --storyboard-border-color: #0a0909;

  --card-border-radius: 0.6rem;
  --input-border-radius: 0.4rem;

  --step-title-color: #f7f8f8;
  --step-title-color-low: rgba(247, 248, 248, 0.1);
  --step-title-color-current: #5f6164;

  --date-color: rgba(247, 248, 248, 0.5);
  --date-border-color: var(--date-color);
  --date-bg-color-hover: #3d3e41;
  --date-bg-dark-color-hover: #737579;
  --date-dark-color: #42454b;
  --date-dark-color-hover: #b4b4b4;

  --select-bg-color: #1f2023;
  --select-border-color: #707070;
  --select-text-color: #8a8f98;

  --icon-fill: #bebebe;
  --icon-button-hover: #3d3e41;

  --header-dropdown-hover: #3d3e41;
  --header-dropdown-date-color: #8a8f98;

  --input-add-comment-text-color: #f7f8f8;
  --input-add-comment-bg-color: #42454b;

  --dialog-contant-bg-color: #1f1f22;

  --row-selected-color: #ffffff08;

  --strip-height: 6.4rem;

  --shot-strip-height: 12.5rem;

  --border-radius-small: 0.4rem;
  --border-radius-medium: 0.6rem;
  --border-radius-large: 0.8rem;
  --shooting-day-time-color: #8a8f98;
  --shooting-day-table-color-header: #5f6164;
  --shooting-day-table--bg-color: #42454b;
  --shooting-day-table-empty-bg-color: rgba(66, 69, 75, 0.3);
  --shooting-day-table-text-color: #8a8f98;
  --white-default: #ffffff;

  --grid-columns-2: repeat(2, 1fr);
  --grid-columns-3: repeat(3, 1fr);
  --grid-columns-4: repeat(4, 1fr);
  --grid-columns-5: repeat(5, 1fr);
  --grid-columns-6: repeat(6, 1fr);

  /* Texts */
  --color-texts-low-contrast: var(--color-grayscale-manatee);
  --color-texts-medium-contrast: var(--color-grayscale-light-slate);
  --color-texts-middle-contrast: var(--color-grayscale-ghost);
  --color-texts-high-contrast: var(--color-grayscale-white);

  /* Grayscale scale, mirrored from apps/webapp/src/ds-colors.css. The --color-texts-*
     aliases below already reference four of these; without them they resolve to nothing. */
  --color-grayscale-woodsmoke: #0c0d0f;
  --color-grayscale-eerie-black: #181a1e;
  --color-grayscale-shark: #1d1f24;
  --color-grayscale-charleston: #25272d;
  --color-grayscale-tuna: #31343c;
  --color-grayscale-arsenic: #3c404a;
  --color-grayscale-trout: #494e5a;
  --color-grayscale-shuttle: #626878;
  --color-grayscale-light-slate: #7a8296;
  --color-grayscale-manatee: #959bab;
  --color-grayscale-cadet: #afb4c0;
  --color-grayscale-ghost: #cacdd5;
  --color-grayscale-mischka: #d7dae0;
  --color-grayscale-athens: #e4e6ea;
  --color-grayscale-anti-flash-white: #f2f3f5;
  --color-grayscale-white: #fafafa;

  /* Surfaces */
  --color-surfaces-app-background-0: var(--color-grayscale-shark);
  --color-surfaces-bg-elevation-1: var(--color-grayscale-charleston);
  --color-surfaces-bg-elevation-2: var(--color-grayscale-tuna);
  --color-surfaces-bg-elevation-3: var(--color-grayscale-arsenic);
  --color-surfaces-bg-elevation-4: var(--color-grayscale-trout);
  --color-surfaces-bg-elevation-5: var(--color-grayscale-shuttle);
  --color-surfaces-bg-recessed: var(--color-grayscale-eerie-black);

  /* Layers */
  --layer-modal: 100001;

  /* Neutrals, mirrored from apps/webapp/src/ds-color-primitives.css. */
  --color-neutrals-000: #ffffff;
  --color-neutrals-100: #f5f5f5;
  --color-neutrals-200: #eaecee;
  --color-neutrals-300: #ced4da;
  --color-neutrals-400: #adb5bd;
  --color-neutrals-500: #6c757d;
  --color-neutrals-600: #494e55;
  --color-neutrals-700: #25262c;
  --color-neutrals-800: #1d1d23;
  --color-neutrals-900: #14141a;

  /* Semantic aliases over the neutrals scale, matching the app's dark theme.
     Webapp re-declares these under html[data-theme=…], which outranks :root, so
     its dark and light themes keep winning. */
  --color-surface-primary: var(--color-neutrals-900);
  --color-surface-secondary: var(--color-neutrals-800);
  --color-surface-tertiary: var(--color-neutrals-700);

  --color-text-primary: var(--color-neutrals-000);
  --color-text-secondary: var(--color-neutrals-200);
  --color-text-tertiary: var(--color-neutrals-300);

  --color-stroke-default: var(--color-neutrals-700);
  --color-stroke-hover: var(--color-neutrals-600);

  /* Spacing primitives, mirrored from apps/webapp/src/ds-spacing-primitives.css. */
  --primitives-spacing-none: 0;
  --primitives-spacing-xxs: 0.2rem;
  --primitives-spacing-xs: 0.4rem;
  --primitives-spacing-sm: 0.8rem;
  --primitives-spacing-md: 1.2rem;
  --primitives-spacing-lg: 1.6rem;
  --primitives-spacing-xl: 2.4rem;
  --primitives-spacing-xxl: 3.2rem;
  --primitives-spacing-3xl: 4rem;
  --primitives-spacing-4xl: 4.8rem;
  --primitives-spacing-5xl: 6.4rem;
  --primitives-spacing-6xl: 8rem;
  --primitives-spacing-7xl: 9.6rem;
  --primitives-spacing-8xl: 11.2rem;
  --primitives-spacing-9xl: 12rem;

  /* Semantic spacing. Components (TextInput, FieldLabel, …) read these, so every app gets
     them from here; webapp re-declares them under html[data-theme=…] with the same values. */
  --spacing-radius-xs: var(--primitives-spacing-xs);
  --spacing-radius-sm: var(--primitives-spacing-sm);
  --spacing-radius-md: var(--primitives-spacing-md);
  --spacing-radius-lg: var(--primitives-spacing-lg);
  --spacing-gap-xs: var(--primitives-spacing-xs);
  --spacing-gap-sm: var(--primitives-spacing-sm);
  --spacing-gap-md: var(--primitives-spacing-md);
}

* {
  padding: 0;
  margin: 0;
  box-sizing: border-box;
}

*:focus {
  outline: none;
  box-shadow: none;
}
